import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createUser, updateUser, fetchUserById } from './userSlice';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import log from '../../logger';
import ReactForm from '../../components/ReactForm';
import { paths } from '../../routes/helpers';
import { getImageUrl } from '../../utils/urlUtils';

const UserCreateEdit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userId } = useParams();
  const { loading = false, error = null, items = [] } = useSelector((state) => state.user || {});
  const user = userId ? items.find(u => u._id === userId) : null;
  const [showPassword, setShowPassword] = useState(false);
  const [existingProfilePic, setExistingProfilePic] = useState('');

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserById(userId));
    }
  }, [userId, dispatch]);

  useEffect(() => {
    if (user && user.profilePic) {
      setExistingProfilePic(user.profilePic);
    }
  }, [user]);

  const handleSubmit = async (formData) => {
    try {
      if (userId) {
        // Check if there are files to upload
        const hasFiles = formData.profilePic && formData.profilePic.length > 0;
        
        if (hasFiles) {
          // Use FormData for file uploads
          const formDataToSubmit = new FormData();
          
          Object.keys(formData).forEach(key => {
            if (formData[key] !== undefined && formData[key] !== null && key !== 'profilePic') {
              formDataToSubmit.append(key, formData[key]);
            }
          });
          
          // Add the file
          formDataToSubmit.append('profilePic', formData.profilePic[0]);
          
          await dispatch(updateUser({ id: userId, formData: formDataToSubmit })).unwrap();
        } else {
          // Use regular JSON for text-only updates
          const { profilePic, ...textData } = formData;
          await dispatch(updateUser({ id: userId, formData: textData })).unwrap();
        }
        
        toast.success('User updated successfully!');
        log.info('User updated:', formData.username);
      } else {
        // Check if there are files to upload during creation
        const hasFiles = formData.profilePic && formData.profilePic.length > 0;
        
        if (hasFiles) {
          // Use FormData for file uploads during creation
          const formDataToSubmit = new FormData();
          
          Object.keys(formData).forEach(key => {
            if (formData[key] !== undefined && formData[key] !== null && key !== 'profilePic') {
              formDataToSubmit.append(key, formData[key]);
            }
          });
          
          // Add the file
          formDataToSubmit.append('profilePic', formData.profilePic[0]);
          
          await dispatch(createUser(formDataToSubmit)).unwrap();
        } else {
          // Use regular JSON for text-only creation
          const { profilePic, ...textData } = formData;
          await dispatch(createUser(textData)).unwrap();
        }
        
        toast.success('User created successfully!');
        log.info('User created:', formData.username);
      }
      navigate(paths.private.user.list());
    } catch (err) {
      toast.error(err || 'An error occurred');
      log.error('Error creating/updating user:', err);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const fields = [
    {
      name: 'username',
      label: 'Username',
      type: 'text',
      required: false,
      placeholder: 'Enter username'
    },
    {
      name: 'email',
      label: 'Email',
      type: 'email',
      required: true,
      placeholder: 'Enter email'
    },
    {
      name: 'password',
      label: 'Password',
      type: 'password',
      required: !userId,
      placeholder: 'Enter password',
      showPassword: showPassword,
      onTogglePassword: togglePasswordVisibility
    },
    {
      name: 'profilePic',
      label: 'Profile Picture',
      type: 'file',
      required: false,
      accept: 'image/*',
      multiple: false
    },
    {
      name: 'role',
      label: 'Role',
      type: 'select',
      required: true,
      defaultValue: "user",
      options: [
        { value: "user", label: 'User' },
        { value: "business", label: 'Business' },
        { value: "admin", label: 'Admin' }
      ]
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: "ACTIVE",
      options: [
        { value: "ACTIVE", label: 'Active' },
        { value: "INACTIVE", label: 'Inactive' },
        { value: "SUSPENDED", label: 'Suspended' }
      ]
    }
  ];

  const defaultValues = user ? {
    username: user.username,
    email: user.email,
    role: user.role,
    status: user.status
  } : {
    username: '',
    email: '',
    password: '',
    role: 'user',
    status: 'ACTIVE'
  };

  log.info('Default values:', defaultValues);

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">
        {userId ? 'Edit User' : 'Create User'}
      </h2>
      
      {/* Show current profile picture when editing */}
      {userId && existingProfilePic && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Current Profile Picture:</h3>
          <div className="flex items-center space-x-4">
            <img 
              src={getImageUrl(existingProfilePic)} 
              alt="Current profile" 
              className="w-16 h-16 rounded-full object-cover border-2 border-gray-300"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div 
              className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-semibold text-lg border-2 border-gray-300 hidden"
            >
              {(user?.username || user?.email || '?').charAt(0).toUpperCase()}
            </div>
            <div className="text-sm text-gray-600">
              <p>Upload a new image below to replace the current profile picture.</p>
            </div>
          </div>
        </div>
      )}
      
      <ReactForm
        fields={fields}
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
        defaultValues={defaultValues}
        submitText={userId ? 'Update User' : 'Create User'}
      />
    </div>
  );
};

export default UserCreateEdit;
